import React, { useState, useEffect } from "react";
import {
  Save,
  X,
  Pencil,
  Syringe,
  Plus,
  Stethoscope,
  GraduationCap,
  Wrench,
  Snowflake,
  Users,
} from "lucide-react";
import { Paciente } from "../../../types";
import { useAuth } from "../../auth/context/AuthContext";
import {
  ehMedico,
  ehMultiprofissional,
} from "../../../shared/utils/permissoesCategoria";
import { theme } from "../../../shared/styles/theme";
import { usePacientes } from "../../pacientes/context/PacientesContext";

interface EquipeCirurgicaProps {
  paciente: Paciente;
  onSalvarTx?: (dados: any) => void;
}
export default function EquipeCirurgica({
  paciente,
  onSalvarTx,
}: EquipeCirurgicaProps) {
  const { atualizarPaciente } = usePacientes();
  const { usuarioLogado } = useAuth();
  // 🔐 B88 (AI_98): dados do transplante sao campos CLINICOS do doc
  // pacientes - edicao restrita a equipe clinica (espelho das
  // firestore.rules). Nome legado: hoje significa "somente leitura"
  // (vale p/ convidado E administrativo).
  const isConvidado =
    !ehMedico(usuarioLogado) && !ehMultiprofissional(usuarioLogado);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [dados, setDados] = useState<{
    cirurgiao?: string;
    auxiliar1?: string;
    auxiliar2?: string;
    auxiliar3?: string;
    fellow?: string;
    residente?: string;
    academico?: string;
    instrumentador?: string;
    anestesista1?: string;
    anestesista2?: string;
    backTable?: string;
    editadoEm?: string;
  }>({
    cirurgiao: "",
    auxiliar1: "",
    auxiliar2: "",
    auxiliar3: "",
    fellow: "",
    residente: "",
    academico: "",
    instrumentador: "",
    anestesista1: "",
    anestesista2: "",
    backTable: "",
  });

  // ✅ CARREGAR dados do paciente quando o componente montar
  useEffect(() => {
    const equipe = paciente.dadosTransplante?.equipeCirurgica;
    if (
      equipe &&
      Object.values(equipe).some((v) => v && String(v).trim() !== "")
    ) {
      setDados(equipe);
    } else {
      setDados({
        cirurgiao: "",
        auxiliar1: "",
        auxiliar2: "",
        auxiliar3: "",
        fellow: "",
        residente: "",
        academico: "",
        instrumentador: "",
        anestesista1: "",
        anestesista2: "",
        backTable: "",
      });
    }
  }, [paciente.id, paciente.dadosTransplante?.equipeCirurgica]);

  // ✅ Verificar se tem dados salvos
  const temDados = Object.values(dados).some(
    (valor) => valor && valor.trim() !== ""
  );
  const handleSalvar = () => {
    const temDadosPreenchidos = Object.entries(dados).some(
      ([k, v]) => k !== "editadoEm" && v && String(v).trim() !== ""
    );
    if (!temDadosPreenchidos) {
      alert("Preencha pelo menos um campo antes de salvar.");
      return;
    }
    const dadosComTimestamp = { ...dados, editadoEm: new Date().toISOString() };
    if (onSalvarTx) {
      onSalvarTx(dadosComTimestamp);
    } else {
      atualizarPaciente(paciente.id, {
        dadosTransplante: {
          ...paciente.dadosTransplante,
          equipeCirurgica: dadosComTimestamp,
        },
      });
    }
    setModoEdicao(false);
  };
  // Janela de 24h para edição
  const editadoEm = (paciente.dadosTransplante as any)
    ?.equipeCirurgicaEditadoEm;
  const horasDecorridas = editadoEm
    ? (new Date().getTime() - new Date(editadoEm).getTime()) / (1000 * 60 * 60)
    : 0;
  const podeEditar = !temDados || horasDecorridas <= 24;

  const campos: { key: string; label: string; icon: React.ElementType }[] = [
    { key: "cirurgiao", label: "Cirurgião", icon: Stethoscope },
    { key: "auxiliar1", label: "1º Auxiliar", icon: Stethoscope },
    { key: "auxiliar2", label: "2º Auxiliar", icon: Stethoscope },
    { key: "auxiliar3", label: "3º Auxiliar", icon: Stethoscope },
    { key: "fellow", label: "Fellow", icon: Stethoscope },
    { key: "residente", label: "Residente", icon: Stethoscope },
    { key: "academico", label: "Acadêmico", icon: GraduationCap },
    { key: "instrumentador", label: "Instrumentador", icon: Wrench },
    { key: "anestesista1", label: "1º Anestesista", icon: Syringe },
    { key: "anestesista2", label: "2º Anestesista", icon: Syringe },
    { key: "backTable", label: "Back Table", icon: Snowflake },
  ];

  return (
    <div>
      {/* BOTÕES */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "16px",
          gap: "8px",
        }}
      >
        {!isConvidado && (
          <>
            {modoEdicao ? (
              <>
                <button
                  onClick={handleSalvar}
                  style={{
                    padding: "8px 16px",
                    background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                    color: "white",
                    border: "none",
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <Save size={14} style={{ marginRight: "6px" }} /> Salvar
                </button>
                <button
                  onClick={() => {
                    setModoEdicao(false);
                    if (paciente.dadosTransplante?.equipeCirurgica) {
                      setDados(paciente.dadosTransplante.equipeCirurgica);
                    }
                  }}
                  style={{
                    padding: "8px 16px",
                    background: theme.gray[300],
                    color: theme.gray[700],
                    border: "none",
                    borderRadius: theme.borderRadius.md,
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <X size={14} style={{ marginRight: "6px" }} /> Cancelar
                </button>
              </>
            ) : podeEditar ? (
              <button
                onClick={() => setModoEdicao(true)}
                style={{
                  padding: "8px 16px",
                  background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                  color: "white",
                  border: "none",
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <Pencil size={14} style={{ marginRight: "6px" }} /> Editar
              </button>
            ) : null}
          </>
        )}
      </div>

      {/* MODO VISUALIZAÇÃO - CARD COM A EQUIPE */}
      {!modoEdicao && temDados ? (
        <div
          style={{
            background: `linear-gradient(135deg, ${theme.primary[50]} 0%, white 100%)`,
            border: `2px solid ${theme.primary[200]}`,
            borderRadius: theme.borderRadius.lg,
            padding: "20px",
          }}
        >
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: theme.primary[700],
              marginBottom: "20px",
              fontFamily: theme.fonts.sans,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <Users size={16} style={{ marginRight: "8px" }} /> Equipe Cirúrgica
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "16px",
            }}
          >
            {campos.map((campo) => {
              const valor = dados[campo.key as keyof typeof dados];
              if (!valor) return null;

              return (
                <div
                  key={campo.key}
                  style={{
                    background: "white",
                    padding: "12px 16px",
                    borderRadius: theme.borderRadius.md,
                    border: `1px solid ${theme.gray[200]}`,
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "24px",
                      flexShrink: 0,
                    }}
                  >
                    <campo.icon size={24} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: "11px",
                        color: theme.gray[600],
                        fontFamily: theme.fonts.sans,
                        marginBottom: "2px",
                        textTransform: "uppercase",
                        fontWeight: "600",
                      }}
                    >
                      {campo.label}
                    </div>
                    <div
                      style={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: theme.gray[800],
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {valor}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : !modoEdicao && !temDados ? (
        // ESTADO VAZIO
        <div
          style={{
            textAlign: "center",
            padding: "48px 20px",
            background: theme.gray[50],
            borderRadius: theme.borderRadius.lg,
            border: `2px dashed ${theme.gray[300]}`,
          }}
        >
          <Users
            size={48}
            color={theme.gray[300]}
            style={{ marginBottom: "16px" }}
          />
          <p
            style={{
              color: theme.gray[600],
              fontSize: "14px",
              fontFamily: theme.fonts.sans,
              marginBottom: "16px",
            }}
          >
            Nenhuma equipe cirúrgica cadastrada ainda
          </p>
          {!isConvidado && (
            <button
              onClick={() => setModoEdicao(true)}
              style={{
                padding: "10px 20px",
                background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[700]} 100%)`,
                color: "white",
                border: "none",
                borderRadius: theme.borderRadius.md,
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                fontFamily: theme.fonts.sans,
              }}
            >
              <Plus size={14} style={{ marginRight: "6px" }} /> Cadastrar Equipe
            </button>
          )}
        </div>
      ) : (
        // MODO EDIÇÃO - FORMULÁRIO
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "16px",
          }}
        >
          {campos.map((campo) => (
            <div key={campo.key}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.gray[700],
                  marginBottom: "8px",
                  fontFamily: theme.fonts.sans,
                }}
              >
                <campo.icon size={14} style={{ marginRight: "6px" }} />{" "}
                {campo.label}
              </label>
              <input
                type="text"
                value={dados[campo.key as keyof typeof dados]}
                onChange={(e) =>
                  setDados({ ...dados, [campo.key]: e.target.value })
                }
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `2px solid ${theme.gray[300]}`,
                  borderRadius: theme.borderRadius.md,
                  fontSize: "14px",
                  fontFamily: theme.fonts.sans,
                  boxSizing: "border-box",
                }}
                placeholder={`Nome do(a) ${campo.label.toLowerCase()}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
